import type { QuizScript } from '../types'
import type { QuizMediaObject } from '../types/legacyQuiz'
import { QuizRenderer } from '../components/quiz-types/quiz-renderer'
import { DebugQuizId } from '../components/quiz-types/shared/debug-quiz-id'
import {
  ImageScaleMultiplierProvider,
  QuizNumberColorProvider,
  AdditionalImageScalesProvider,
  OptionImageScaleProvider,
} from '../components/quiz-types/print/print-common'
import { PrintQuizDescription } from '../components/quiz-types/print'
import { toQuizData, LAYOUT } from './constants'

// Step별 문제 번호 색상 (1-based step index 기준: step1, step2, step3)
const STEP_NUMBER_COLORS: Record<number, string> = {
  0: '#FF9F19', // Step 1 — Orange
  1: '#0066CC', // Step 2 — Blue
  2: '#00BFA0', // Step 3 — Teal
}

export function PrintQuizItem({ script, number, stepIdx = 0, compact, imageScale, additionalImageScales, optionImageScale, spacingOverride, highlighted, onSelect, showCorrectAnswers, correctAnswers, showDescription }: {
  script: QuizScript
  number: number
  stepIdx?: number
  compact?: boolean
  imageScale?: number
  additionalImageScales?: number | Record<number, number>
  optionImageScale?: number
  spacingOverride?: number
  highlighted?: boolean
  onSelect?: () => void
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  showDescription?: boolean
}) {
  const quizData = toQuizData(script)
  const quizId = String(script.quizId ?? '')
  const isStep2 = stepIdx === 1
  const numberColor = STEP_NUMBER_COLORS[stepIdx]

  const defaultSpacing = isStep2 ? 24 : LAYOUT.questionSpacing
  const finalSpacing = spacingOverride ?? defaultSpacing

  const content = (
    <QuizRenderer
      key={quizId}
      quizType={quizData.type}
      quizId={quizId}
      quiz={quizData}
      mode="print"
      number={number}
      hideSubmitButton
      disableSound
      compact={compact}
      showCorrectAnswers={showCorrectAnswers}
      correctAnswers={correctAnswers}
    />
  )

  return (
    <div
      className={`break-inside-avoid${highlighted ? ' outline outline-2 outline-offset-2 outline-blue-500 rounded print:outline-none' : ''}`}
      style={{
        paddingBottom: `${finalSpacing}px`,
        pageBreakInside: 'avoid',
        position: 'relative',
      }}
      onClick={onSelect}
    >
      <div className="print:hidden" style={{ position: 'absolute', top: 0, right: 0, zIndex: 10 }}>
        <DebugQuizId quizId={quizId} />
      </div>
      <div
        className="print-quiz-wrapper"
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <div style={{
          width: '100%',
          maxWidth: 650,
          ...(isStep2 && {
            border: '2px solid #E6E6E6',
            borderRadius: '16px',
            padding: '20px 16px 32px 16px',
          }),
        }}>
          <QuizNumberColorProvider value={numberColor}>
            <OptionImageScaleProvider value={optionImageScale ?? 1}>
              <AdditionalImageScalesProvider value={additionalImageScales ?? 1}>
                {imageScale != null && imageScale !== 1
                  ? <ImageScaleMultiplierProvider value={imageScale}>{content}</ImageScaleMultiplierProvider>
                  : content
                }
                {showDescription && (
                  <PrintQuizDescription
                    description={script.description}
                    commentaryImages={script.commentaryImages as QuizMediaObject[] | null | undefined}
                    vars={script.vars}
                    compact={compact}
                  />
                )}
              </AdditionalImageScalesProvider>
            </OptionImageScaleProvider>
          </QuizNumberColorProvider>
        </div>
      </div>
    </div>
  )
}
